import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Check, Pencil, Plus, Save, Trash2, X } from "lucide-react";

import {
  createWorkflowSequence,
  deleteWorkflowSequence,
  listWorkflowSequences,
  renameWorkflowSequence,
  startWorkflow,
  updateWorkflowStages,
  type WorkflowSequence,
  type WorkflowSequenceStage,
  type WorkflowStage,
} from "@/api/submission_workflow";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const MAX_STAGES = 20;
const UNASSIGNED = "none";

export type WorkflowBuilderMode = "start" | "edit";

// Matches SitePlan::AdvanceWorkflow: full replace before any decision; after
// that only pending (later) stages can change.
export function lockedWorkflowStageCount(stages: Pick<WorkflowStage, "status" | "position">[]): number {
  const decided = stages.some((stage) => stage.status === "approved" || stage.status === "changes_requested");
  const current = stages.find((stage) => stage.status === "in_progress");
  if (decided || (current?.position ?? 0) > 1) {
    return stages.filter((stage) => stage.status !== "pending").length;
  }
  return 0;
}

type WorkflowBuilderDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  municipalityId: number;
  submissionId: number;
  mode?: WorkflowBuilderMode;
  existingStages?: WorkflowStage[];
  onComplete: () => void;
};

export function WorkflowBuilderDialog({
  open,
  onOpenChange,
  municipalityId,
  submissionId,
  mode = "start",
  existingStages = [],
  onComplete,
}: WorkflowBuilderDialogProps) {
  const queryClient = useQueryClient();
  const sequencesKey = ["workflow_sequences", municipalityId] as const;
  const lockedCount = mode === "edit" ? lockedWorkflowStageCount(existingStages) : 0;
  const showSequencePicker = lockedCount === 0;

  const { data } = useQuery({
    queryKey: sequencesKey,
    queryFn: () => listWorkflowSequences(municipalityId),
    enabled: open,
  });
  const sequences = useMemo(() => data?.sequences ?? [], [data?.sequences]);
  const members = data?.members ?? [];

  const [selectedSequenceId, setSelectedSequenceId] = useState<number | null>(null);
  const [stages, setStages] = useState<WorkflowSequenceStage[]>([]);
  const [edited, setEdited] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameDraft, setRenameDraft] = useState("");
  const [savingAs, setSavingAs] = useState(false);
  const [saveAsName, setSaveAsName] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const selectedSequence = sequences.find((sequence) => sequence.id === selectedSequenceId) ?? null;

  const loadSequence = (sequence: WorkflowSequence | null) => {
    setSelectedSequenceId(sequence?.id ?? null);
    setStages(sequence ? sequence.stages.map((stage) => ({ ...stage })) : [{ name: "", assignee_id: null }]);
    setEdited(false);
    setRenaming(false);
    setSavingAs(false);
    setErrorMessage(null);
  };

  // The picker defaults to the most recently used sequence when the dialog
  // opens for a new workflow. Edit mode loads the live stage list instead.
  // Initialize exactly once per open: refetches while the dialog stays
  // open (rename, save-as, delete) must not wipe in-progress stage edits.
  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    if (!open) {
      setInitialized(false);
      return;
    }
    if (initialized) return;

    if (mode === "edit") {
      setSelectedSequenceId(null);
      setStages(
        existingStages.length > 0
          ? existingStages.map((stage) => ({ name: stage.name, assignee_id: stage.assignee_id }))
          : [{ name: "", assignee_id: null }],
      );
      setEdited(false);
      setRenaming(false);
      setSavingAs(false);
      setErrorMessage(null);
      setInitialized(true);
      return;
    }

    if (!data) return;
    loadSequence(sequences[0] ?? null);
    setInitialized(true);
  }, [open, initialized, data, sequences, mode, existingStages]);

  const mutateStages = (updater: (current: WorkflowSequenceStage[]) => WorkflowSequenceStage[]) => {
    setStages(updater);
    setEdited(true);
  };

  const namedStages = () => stages.filter((stage) => stage.name.trim().length > 0);

  const saveMutation = useMutation({
    mutationFn: () => {
      const named = namedStages();
      if (mode === "edit") {
        return updateWorkflowStages(municipalityId, submissionId, named);
      }
      return startWorkflow(
        municipalityId,
        submissionId,
        named,
        // Only credit the sequence when it was used as-is.
        selectedSequenceId != null && !edited ? selectedSequenceId : null,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["submission_workflow", submissionId] });
      queryClient.invalidateQueries({ queryKey: sequencesKey });
      onComplete();
      onOpenChange(false);
    },
    onError: (mutationError: Error) => setErrorMessage(mutationError.message),
  });

  const saveAsMutation = useMutation({
    mutationFn: () => createWorkflowSequence(municipalityId, saveAsName.trim(), namedStages()),
    onSuccess: (sequence) => {
      queryClient.invalidateQueries({ queryKey: sequencesKey });
      setSelectedSequenceId(sequence.id);
      setEdited(false);
      setSavingAs(false);
      setSaveAsName("");
    },
    onError: (mutationError: Error) => setErrorMessage(mutationError.message),
  });

  const renameMutation = useMutation({
    mutationFn: () => renameWorkflowSequence(municipalityId, selectedSequenceId as number, renameDraft.trim()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sequencesKey });
      setRenaming(false);
    },
    onError: (mutationError: Error) => setErrorMessage(mutationError.message),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteWorkflowSequence(municipalityId, selectedSequenceId as number),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sequencesKey });
      loadSequence(null);
    },
    onError: (mutationError: Error) => setErrorMessage(mutationError.message),
  });

  const namedStageCount = namedStages().length;
  const editing = mode === "edit";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="workflow-builder-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit approval stages" : "Start approval workflow"}</DialogTitle>
          <DialogDescription>
            {lockedCount > 0
              ? "Earlier stages are locked because they have already started or been decided. You can still change later stages."
              : "Pick a saved sequence or build one for this plan. Edits here never change the saved sequence."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {showSequencePicker ? (
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground">Sequence</span>
              <Select
                value={selectedSequenceId != null ? String(selectedSequenceId) : "adhoc"}
                onValueChange={(value) => {
                  if (value !== "adhoc") {
                    loadSequence(sequences.find((s) => s.id === Number(value)) ?? null);
                    return;
                  }
                  if (editing) {
                    setSelectedSequenceId(null);
                    setStages(
                      existingStages.length > 0
                        ? existingStages.map((stage) => ({ name: stage.name, assignee_id: stage.assignee_id }))
                        : [{ name: "", assignee_id: null }],
                    );
                    setEdited(false);
                    return;
                  }
                  loadSequence(null);
                }}
              >
                <SelectTrigger className="h-8 w-56 text-xs" aria-label="Saved sequence">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sequences.map((sequence) => (
                    <SelectItem key={sequence.id} value={String(sequence.id)}>
                      {sequence.name}
                    </SelectItem>
                  ))}
                  <SelectItem value="adhoc">{editing ? "Keep current stages" : "Start from scratch"}</SelectItem>
                </SelectContent>
              </Select>
              {selectedSequence ? (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2"
                    aria-label="Rename sequence"
                    onClick={() => {
                      setRenaming(true);
                      setRenameDraft(selectedSequence.name);
                    }}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2 text-destructive"
                    aria-label="Delete sequence"
                    disabled={deleteMutation.isPending}
                    onClick={() => deleteMutation.mutate()}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                  <span className="ml-auto text-xs text-muted-foreground">
                    used {selectedSequence.use_count} {selectedSequence.use_count === 1 ? "time" : "times"}
                  </span>
                </>
              ) : null}
            </div>
          ) : null}

          {renaming ? (
            <div className="flex items-center gap-2">
              <Input
                value={renameDraft}
                onChange={(event) => setRenameDraft(event.target.value)}
                className="h-8 text-xs"
                aria-label="New sequence name"
              />
              <Button
                type="button"
                size="sm"
                disabled={!renameDraft.trim() || renameMutation.isPending}
                onClick={() => renameMutation.mutate()}
              >
                <Check className="mr-1 h-3.5 w-3.5" />
                Rename
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => setRenaming(false)}>
                Cancel
              </Button>
            </div>
          ) : null}

          <ol className="space-y-2" data-testid="workflow-builder-stages">
            {stages.map((stage, index) => {
              const locked = index < lockedCount;
              return (
                <li key={index} className="flex items-center gap-2">
                  <span className="w-5 text-right text-xs text-muted-foreground">{index + 1}</span>
                  <Input
                    value={stage.name}
                    placeholder="Stage name"
                    className="h-8 flex-1 text-sm"
                    aria-label={`Stage ${index + 1} name`}
                    disabled={locked}
                    onChange={(event) =>
                      mutateStages((current) =>
                        current.map((s, i) => (i === index ? { ...s, name: event.target.value } : s)),
                      )
                    }
                  />
                  <Select
                    value={stage.assignee_id != null ? String(stage.assignee_id) : UNASSIGNED}
                    disabled={locked}
                    onValueChange={(value) =>
                      mutateStages((current) =>
                        current.map((s, i) =>
                          i === index ? { ...s, assignee_id: value === UNASSIGNED ? null : Number(value) } : s,
                        ),
                      )
                    }
                  >
                    <SelectTrigger className="h-8 w-44 text-xs" aria-label={`Stage ${index + 1} assignee`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                      {members.map((member) => (
                        <SelectItem key={member.id} value={String(member.id)}>
                          {member.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 px-1.5"
                    aria-label={`Move stage ${index + 1} up`}
                    disabled={index <= lockedCount}
                    onClick={() =>
                      mutateStages((current) => {
                        const next = [...current];
                        [next[index - 1], next[index]] = [next[index], next[index - 1]];
                        return next;
                      })
                    }
                  >
                    <ArrowUp className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 px-1.5"
                    aria-label={`Move stage ${index + 1} down`}
                    disabled={index === stages.length - 1 || locked}
                    onClick={() =>
                      mutateStages((current) => {
                        const next = [...current];
                        [next[index], next[index + 1]] = [next[index + 1], next[index]];
                        return next;
                      })
                    }
                  >
                    <ArrowDown className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 px-1.5 text-muted-foreground"
                    aria-label={`Remove stage ${index + 1}`}
                    disabled={stages.length === 1 || locked}
                    onClick={() => mutateStages((current) => current.filter((_, i) => i !== index))}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </li>
              );
            })}
          </ol>

          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={stages.length >= MAX_STAGES}
            onClick={() => mutateStages((current) => [...current, { name: "", assignee_id: null }])}
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Add stage
          </Button>

          {savingAs ? (
            <div className="flex items-center gap-2">
              <Input
                value={saveAsName}
                onChange={(event) => setSaveAsName(event.target.value)}
                placeholder="Sequence name (e.g. Commercial Full Review)"
                className="h-8 text-xs"
                aria-label="Sequence name"
              />
              <Button
                type="button"
                size="sm"
                disabled={!saveAsName.trim() || namedStageCount === 0 || saveAsMutation.isPending}
                onClick={() => saveAsMutation.mutate()}
              >
                <Check className="mr-1 h-3.5 w-3.5" />
                Save
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => setSavingAs(false)}>
                Cancel
              </Button>
            </div>
          ) : null}

          {errorMessage ? <p className="text-sm text-destructive">{errorMessage}</p> : null}

          <div className="flex items-center gap-2 border-t pt-3">
            <span className="text-xs text-muted-foreground">
              {namedStageCount} {namedStageCount === 1 ? "stage" : "stages"}
              {edited ? " (edited)" : ""}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={namedStageCount === 0}
              onClick={() => setSavingAs(true)}
            >
              <Save className="mr-1.5 h-3.5 w-3.5" />
              Save as sequence
            </Button>
            <div className="flex-1" />
            <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={namedStageCount === 0 || saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
            >
              {editing
                ? saveMutation.isPending
                  ? "Saving…"
                  : "Save stages"
                : saveMutation.isPending
                  ? "Starting…"
                  : "Start workflow"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
